/**
 * ThumbnailStrip
 *
 * Horizontally-scrolling strip of slide thumbnails rendered above the
 * presentation toolbar.  Lets the presenter jump straight to any slide
 * by clicking its thumb instead of typing a number+Enter or pressing
 * arrow keys repeatedly.
 *
 * Visibility is driven by the parent (`PresentationMode`) — the strip
 * returns `null` when `visible=false` (simpler than toggling opacity and
 * pointer-events) and is expected to follow the same auto-hide timer
 * as the bottom toolbar so the audience isn't distracted by persistent
 * chrome.
 *
 * Each thumb stops click propagation so the container's click-to-next
 * handler doesn't fire when the presenter picks a thumb.  The active
 * thumb is auto-scrolled into view whenever `currentIndex` changes.
 *
 * Accessibility:
 *   - Container: `aria-label="Slide thumbnails"`, `role="tablist"` on
 *     the inner strip so SR users can tab through slides.
 *   - Each thumb is `role="tab"` with `aria-selected` reflecting the
 *     active index.
 */
import { useEffect, useRef } from "react"

import { useTranslation } from "react-i18next"

import type { BuilderSlide } from "../../types"

import { SlideThumbnailImage } from "../SlideThumbnailImage"

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ThumbnailStripProps {
  slides: BuilderSlide[]
  currentIndex: number
  onJump: (index: number) => void
  visible: boolean
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ThumbnailStrip({ slides, currentIndex, onJump, visible }: ThumbnailStripProps) {
  const { t } = useTranslation()
  const listRef = useRef<HTMLDivElement | null>(null)

  // Autoscroll the active thumb into view on index change.  Using
  // `block: "nearest"` avoids vertical jumps in the outer overlay and
  // `inline: "center"` keeps the active thumb visually centered in the
  // horizontal scroller.
  useEffect(() => {
    if (!visible) return
    const list = listRef.current
    if (!list) return
    const active = list.querySelector<HTMLElement>(`[data-thumb-index='${currentIndex}']`)
    if (active) {
      active.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" })
    }
  }, [currentIndex, visible])

  if (!visible) return null

  return (
    <div
      aria-label={t("slideSense.present.thumbnailsLabel")}
      className="pointer-events-auto absolute bottom-24 left-1/2 z-[60] max-w-[90vw] -translate-x-1/2"
      data-testid="presentation-thumbnail-strip"
    >
      <div
        ref={listRef}
        className="flex gap-2 overflow-x-auto rounded-lg bg-black/70 p-2 backdrop-blur"
        role="tablist"
      >
        {slides.map((s, i) => {
          const isActive = i === currentIndex
          return (
            <button
              key={s.id}
              aria-label={t("slideSense.present.jumpToSlide", { n: i + 1 })}
              aria-selected={isActive}
              data-testid={`presentation-thumbnail-${i}`}
              data-thumb-index={i}
              role="tab"
              style={{ aspectRatio: "16 / 9", width: "7rem" }}
              type="button"
              className={`flex-shrink-0 overflow-hidden rounded bg-black transition-shadow ${
                isActive ? "ring-success ring-2" : "ring-1 ring-white/20 hover:ring-white/50"
              }`}
              onClick={(e) => {
                e.stopPropagation()
                onJump(i)
              }}
            >
              <SlideThumbnailImage
                alt={s.slide.ai_title || t("slideSense.present.slideFallback", { n: i + 1 })}
                className="h-full w-full object-cover"
                slideId={s.slide.id}
                thumbnailUrl={s.slide.thumbnail_url}
              />
            </button>
          )
        })}
      </div>
    </div>
  )
}
